(() => {
  const ROOT_SELECTOR = '[data-cm-cart-section]';
  const MUTATION_CONTROL_SELECTOR = [
    '[data-cm-cart-minus]',
    '[data-cm-cart-plus]',
    '[data-cm-cart-input]',
    '[data-cm-cart-remove]',
    '[data-cm-cart-checkout]',
  ].join(',');

  const parseJsonResponse = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  const replaceToken = (template, token, value) =>
    String(template || '').split(token).join(String(value));

  const findLine = (scope, focus) => {
    const lines = [...scope.querySelectorAll('[data-cm-cart-line]')];
    return (
      lines.find((line) => line.dataset.lineKey === focus.key) ||
      lines.find((line) => Number(line.dataset.lineIndex) === focus.index) ||
      null
    );
  };

  const restoreFocus = (content, focus) => {
    window.requestAnimationFrame(() => {
      const lines = [...content.querySelectorAll('[data-cm-cart-line]')];

      if (focus.control === 'remove') {
        if (lines.length === 0) {
          content.querySelector('[data-cm-empty-focus]')?.focus({ preventScroll: true });
          return;
        }

        const adjacentLine = lines[Math.min(focus.index - 1, lines.length - 1)];
        adjacentLine?.querySelector('h2 a, [data-cm-cart-input]')?.focus({ preventScroll: true });
        return;
      }

      const line = findLine(content, focus);
      if (!line) {
        content.querySelector('[data-cm-empty-focus], h1')?.focus({ preventScroll: true });
        return;
      }

      const preferred = line.querySelector(`[data-cm-cart-${focus.control}]`);
      const fallback = line.querySelector('[data-cm-cart-input], h2 a');
      const target = preferred && !preferred.disabled ? preferred : fallback;
      target?.focus({ preventScroll: true });
    });
  };

  const initialise = (scope = document) => {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...(scope.querySelectorAll?.(ROOT_SELECTOR) || []));
    roots.forEach(bind);
  };

  function bind(root) {
    if (!root || root.dataset.cmBound === 'true') return;

    root.dataset.cmBound = 'true';
    document.documentElement.classList.add('js');

    let pending = false;
    let keepControlsDisabled = false;

    const messages = {
      bagUpdated: root.dataset.cmMessageBagUpdated || '',
      itemRemoved: root.dataset.cmMessageItemRemoved || '',
      quantityOne: root.dataset.cmMessageQuantityOne || '',
      quantityOther: root.dataset.cmMessageQuantityOther || '',
      updateError: root.dataset.cmMessageUpdateError || '',
      updateUnknown: root.dataset.cmMessageUpdateUnknown || '',
      updateUnknownReload: root.dataset.cmMessageUpdateUnknownReload || '',
      refreshError: root.dataset.cmMessageRefreshError || '',
    };

    const announce = (message) => {
      const status = root.querySelector('[data-cm-cart-status]');
      if (!status || !message) return;
      status.textContent = '';
      window.requestAnimationFrame(() => {
        status.textContent = message;
      });
    };

    const syncDisclosure = (details) => {
      if (!(details instanceof HTMLDetailsElement)) return;
      details.querySelector('summary')?.setAttribute('aria-expanded', String(details.open));
    };

    root.querySelectorAll('[data-cm-cart-disclosure]').forEach(syncDisclosure);
    root.addEventListener(
      'toggle',
      (event) => {
        const details = event.target;
        if (details instanceof HTMLDetailsElement && details.matches('[data-cm-cart-disclosure]')) {
          syncDisclosure(details);
        }
      },
      true,
    );

    const clearErrors = () => {
      root.querySelectorAll('[data-cm-line-error], [data-cm-cart-error]').forEach((error) => {
        error.textContent = '';
        error.hidden = true;
      });
    };

    const showError = (scope, focus, message) => {
      const line = findLine(scope, focus);
      const error = line?.querySelector('[data-cm-line-error]') || root.querySelector('[data-cm-cart-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
    };

    const setControlsBusy = (busy) => {
      root.toggleAttribute('aria-busy', busy);

      root.querySelectorAll(MUTATION_CONTROL_SELECTOR).forEach((control) => {
        if (control instanceof HTMLButtonElement || control instanceof HTMLInputElement) {
          if (busy) {
            control.dataset.cmWasDisabled = control.disabled ? 'true' : 'false';
            control.disabled = true;
          } else if ('cmWasDisabled' in control.dataset) {
            control.disabled = control.dataset.cmWasDisabled === 'true';
            delete control.dataset.cmWasDisabled;
          }
          return;
        }

        if (!(control instanceof HTMLAnchorElement)) return;
        if (busy) {
          control.dataset.cmPreviousTabindex = control.getAttribute('tabindex') || '';
          control.setAttribute('aria-disabled', 'true');
          control.setAttribute('tabindex', '-1');
        } else if ('cmPreviousTabindex' in control.dataset) {
          const previousTabindex = control.dataset.cmPreviousTabindex;
          control.removeAttribute('aria-disabled');
          if (previousTabindex) control.setAttribute('tabindex', previousTabindex);
          else control.removeAttribute('tabindex');
          delete control.dataset.cmPreviousTabindex;
        }
      });
    };

    const parseContent = (html) => {
      if (!html) return null;
      return new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cm-cart-content]');
    };

    const fetchCartContent = async () => {
      const sectionId = root.dataset.sectionId;
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', sectionId);
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`Bag section request failed (${response.status}).`);

      const content = parseContent(await response.text());
      if (!content) throw new Error('Bag section did not render.');
      return content;
    };

    const refreshContent = async (cart) => {
      const sectionId = root.dataset.sectionId;
      let freshContent = parseContent(cart?.sections?.[sectionId]);
      if (!freshContent) freshContent = await fetchCartContent();

      const currentContent = root.querySelector('[data-cm-cart-content]');
      if (!currentContent) throw new Error('Current Bag content is missing.');
      freshContent.querySelectorAll('[data-cm-cart-disclosure]').forEach(syncDisclosure);
      currentContent.replaceWith(freshContent);
      return freshContent;
    };

    const refreshAfterUncertainResult = async (cart, focus, message, fallbackMessage = message) => {
      try {
        const content = await refreshContent(cart);
        showError(content, focus, message);
        restoreFocus(content, focus);
        return true;
      } catch {
        const currentContent = root.querySelector('[data-cm-cart-content]') || root;
        showError(currentContent, focus, fallbackMessage);
        keepControlsDisabled = true;
        return false;
      }
    };

    const successMessage = (cart, focus) => {
      if (focus.control === 'remove') {
        return replaceToken(messages.itemRemoved, '__TITLE__', focus.title);
      }

      const itemCount = Number(cart?.item_count);
      if (!Number.isInteger(itemCount) || itemCount < 0) return messages.bagUpdated;
      if (itemCount === 1) return messages.quantityOne;
      return replaceToken(messages.quantityOther, '__COUNT__', itemCount);
    };

    const updateQuantityButtons = (input) => {
      const line = input.closest('[data-cm-cart-line]');
      if (!line || pending) return;

      const current = Number(input.value);
      const minimum = Number(input.min);
      const maximum = input.max === '' ? null : Number(input.max);
      const increment = Number(input.step) || 1;
      const valid = Number.isInteger(current) && input.checkValidity();
      const minus = line.querySelector('[data-cm-cart-minus]');
      const plus = line.querySelector('[data-cm-cart-plus]');

      if (minus) minus.disabled = !valid || current - increment < minimum;
      if (plus) plus.disabled = !valid || (maximum !== null && current + increment > maximum);
    };

    const change = async (line, quantity, focus) => {
      if (pending) return;

      pending = true;
      keepControlsDisabled = false;
      clearErrors();
      setControlsBusy(true);

      let response;
      try {
        response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: line.dataset.lineKey,
            quantity,
            sections: root.dataset.sectionId,
            sections_url: `${window.location.pathname}${window.location.search}`,
          }),
        });
      } catch {
        document.dispatchEvent(new CustomEvent('cm:cart-updated', { detail: { source: 'cart-unknown' } }));
        await refreshAfterUncertainResult(
          null,
          focus,
          messages.updateUnknown,
          messages.updateUnknownReload,
        );
        pending = false;
        if (keepControlsDisabled) root.removeAttribute('aria-busy');
        else setControlsBusy(false);
        return;
      }

      const cart = await parseJsonResponse(response);

      if (!response.ok) {
        const message = cart?.description || cart?.message || messages.updateError;
        document.dispatchEvent(new CustomEvent('cm:cart-updated', { detail: { source: 'cart-error' } }));
        await refreshAfterUncertainResult(cart, focus, message);
        pending = false;
        if (keepControlsDisabled) root.removeAttribute('aria-busy');
        else setControlsBusy(false);
        return;
      }

      document.dispatchEvent(
        new CustomEvent('cm:cart-updated', {
          detail: {
            cart: cart && Number.isInteger(Number(cart.item_count)) ? cart : null,
            source: 'cart',
          },
        }),
      );
      announce(successMessage(cart, focus));

      try {
        const content = await refreshContent(cart);
        restoreFocus(content, focus);
      } catch {
        const currentContent = root.querySelector('[data-cm-cart-content]') || root;
        showError(currentContent, focus, messages.refreshError);
        keepControlsDisabled = true;
      } finally {
        pending = false;
        if (keepControlsDisabled) root.removeAttribute('aria-busy');
        else setControlsBusy(false);
      }
    };

    root.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const mutationControl = target.closest(MUTATION_CONTROL_SELECTOR);
      if (pending && mutationControl) {
        event.preventDefault();
        return;
      }

      const line = target.closest('[data-cm-cart-line]');
      if (!line || !root.contains(line)) return;

      const input = line.querySelector('[data-cm-cart-input]');
      if (!input) return;

      const baseFocus = {
        key: line.dataset.lineKey,
        index: Number(line.dataset.lineIndex),
        title: line.dataset.lineTitle || '',
      };

      if (target.closest('[data-cm-cart-remove]')) {
        event.preventDefault();
        change(line, 0, { ...baseFocus, control: 'remove' });
        return;
      }

      const current = Number(input.value);
      const minimum = Number(input.min);
      const maximum = input.max === '' ? null : Number(input.max);
      const increment = Number(input.step) || 1;
      if (!Number.isInteger(current) || !input.checkValidity()) {
        input.reportValidity();
        return;
      }

      if (target.closest('[data-cm-cart-minus]')) {
        event.preventDefault();
        const next = current - increment;
        if (next >= minimum) change(line, next, { ...baseFocus, control: 'minus' });
      } else if (target.closest('[data-cm-cart-plus]')) {
        event.preventDefault();
        const next = current + increment;
        if (maximum === null || next <= maximum) change(line, next, { ...baseFocus, control: 'plus' });
      }
    });

    root.addEventListener('change', (event) => {
      const input = event.target instanceof HTMLInputElement ? event.target : null;
      if (!input?.matches('[data-cm-cart-input]')) return;

      if (!input.checkValidity()) {
        input.reportValidity();
        return;
      }

      const line = input.closest('[data-cm-cart-line]');
      const quantity = Number(input.value);
      if (!line || !Number.isInteger(quantity)) return;
      if (quantity === Number(input.dataset.currentQuantity)) return;

      change(line, quantity, {
        key: line.dataset.lineKey,
        index: Number(line.dataset.lineIndex),
        title: line.dataset.lineTitle || '',
        control: 'input',
      });
    });

    root.addEventListener('input', (event) => {
      const input = event.target instanceof HTMLInputElement ? event.target : null;
      if (input?.matches('[data-cm-cart-input]')) updateQuantityButtons(input);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise(), { once: true });
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
